import { useId, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { IconTile } from "./IconTile";

/**
 * One of a few paths the attorney picks between before anything is asked of
 * them: an icon in a tinted square, a title over a line saying what the path
 * is for, and a chevron saying it leads somewhere.
 *
 * Not a `Button`, because it is a row of a list that takes a press rather than
 * an act, and not a `MenuItem`, because it sits in a panel the Tab key walks
 * through rather than in a menu the arrow keys do.
 */
export function ChoiceCard({
  icon,
  title,
  description,
  onChoose,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  onChoose: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  return (
    <button
      type="button"
      // Named by the title alone: read whole, the description would be its name too.
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={onChoose}
      className="flex w-full cursor-pointer items-center gap-3 rounded-md border border-border bg-card px-[13px] py-3 text-left transition hover:-translate-y-px hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <IconTile>{icon}</IconTile>
      <span className="min-w-0">
        <span
          id={titleId}
          className="block text-[13px] font-semibold text-foreground"
        >
          {title}
        </span>
        <span
          id={descriptionId}
          className="mt-0.5 block text-[11.5px] text-muted-foreground"
        >
          {description}
        </span>
      </span>
      <ChevronRight
        aria-hidden="true"
        className="ml-auto size-3.5 flex-none text-subtle-foreground"
      />
    </button>
  );
}
